/**
 * Mobile sheet — the app's standard phone-sized overlay container.
 *
 * Two presentations:
 * - `side="bottom"` (default): a bottom sheet that slides up over a dimmed
 *   backdrop — the mobile-app-native replacement for desktop dropdowns/menus
 *   (used by the workspace More menu and the conversation switcher).
 * - `side="left"`: a full-height drawer that slides in from the left (used by
 *   the mobile file explorer).
 *
 * Portals to `document.body` so it always covers the full viewport regardless
 * of where it's mounted. Dismissed by: backdrop tap, Escape, the drag handle
 * (bottom sheets — drag down past a threshold), or the host setting
 * `open={false}`. Content scrolls internally; safe-area insets are padded so
 * rows never hide behind the iOS home indicator.
 *
 * @module
 */

import './MobileSheet.css'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTranslation } from '@molecule/app-react'

import { useHistoryDismissable } from '../hooks/useHistoryDismissable.js'
import { Icon } from './Icon.js'

/** Props for {@link MobileSheet}. */
export interface MobileSheetProps {
  /** Whether the sheet is shown. When false, nothing renders. */
  open: boolean
  /** Called when the user dismisses (backdrop, Escape, drag-down, close button). */
  onClose: () => void
  /** Optional header title. Omitted → no header row (children own the layout). */
  title?: string
  /** Which edge the sheet slides from. Default `bottom`. */
  side?: 'bottom' | 'left'
  /** Stable automation id for the sheet (close button gets `${molId}-close`). */
  molId: string
  /** Sheet content. */
  children: ReactNode
}

/** Drag distance (px) past which releasing dismisses a bottom sheet. */
const DRAG_DISMISS_PX = 80

/**
 * Renders the sheet into a body portal.
 * @param props - Component props.
 * @returns The sheet, or null when closed.
 */
export function MobileSheet({
  open,
  onClose,
  title,
  side = 'bottom',
  molId,
  children,
}: MobileSheetProps) {
  const { t } = useTranslation()
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const dragStartY = useRef<number | null>(null)
  const [dragOffset, setDragOffset] = useState(0)

  // Platform back gesture/button closes the sheet (Android modal convention)
  // instead of navigating the SPA route out from under it.
  useHistoryDismissable(open, onClose)

  // Escape closes. CAPTURE phase so a sheet stacked over another Escape-bound
  // layer (e.g. a full-screen PanelModal, which listens in the bubble phase)
  // closes ALONE — document-level bubble listeners don't shield each other.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, onClose])

  // Reset any in-progress drag when the sheet closes.
  useEffect(() => {
    if (!open) {
      dragStartY.current = null
      setDragOffset(0)
    }
  }, [open])

  const handleBackdropPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  // Drag-to-dismiss on the grab handle (bottom sheets only). Pointer events so
  // touch, pen, and mouse all work; capture keeps the gesture when the finger
  // leaves the handle.
  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragStartY.current = e.clientY
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [])
  const onHandlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current === null) return
    setDragOffset(Math.max(0, e.clientY - dragStartY.current))
  }, [])
  const onHandlePointerUp = useCallback(() => {
    if (dragStartY.current === null) return
    const shouldClose = dragOffset > DRAG_DISMISS_PX
    dragStartY.current = null
    setDragOffset(0)
    if (shouldClose) onClose()
  }, [dragOffset, onClose])

  if (!open) return null

  return createPortal(
    <div
      className={`mol-sheet-backdrop mol-sheet-${side}`}
      onPointerDown={handleBackdropPointerDown}
      data-mol-id={`${molId}-backdrop`}
    >
      <div
        ref={sheetRef}
        className="mol-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-mol-id={molId}
        style={
          dragOffset > 0
            ? { transform: `translateY(${dragOffset}px)`, transition: 'none' }
            : undefined
        }
      >
        {side === 'bottom' && (
          <div
            className="mol-sheet-handle-row"
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerUp}
          >
            <div className="mol-sheet-handle" aria-hidden="true" />
          </div>
        )}
        {title !== undefined && (
          <div className="mol-sheet-header">
            <span className="mol-sheet-title">{title}</span>
            <button
              type="button"
              className="mol-sheet-close"
              data-mol-id={`${molId}-close`}
              aria-label={t('workspace.sheet.close', undefined, { defaultValue: 'Close' })}
              onClick={onClose}
            >
              <Icon name="x-mark" size={18} />
            </button>
          </div>
        )}
        <div className="mol-sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
